import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell,
  TableHead, TableRow, Alert, IconButton, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  MenuItem, Button, Chip, Divider
} from '@mui/material';
import { ArrowBack, Add, Edit, Delete, Category, Label, WarningAmber } from '@mui/icons-material';
import axios from 'axios';
import { useUnsavedGuard } from '../hooks/useUnsavedGuard';

const InfoRow = ({ label, value }) => (
  <Box display="flex" gap={1} mb={0.5}>
    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 160, fontWeight: 500 }}>{label}</Typography>
    <Typography variant="body2">{value || <em style={{ color: '#bbb' }}>—</em>}</Typography>
  </Box>
);

const API = process.env.REACT_APP_API_URL;

const emptyClass    = { classCode: '', className: '', description: '' };
const emptySubclass = { subclassCode: '', subclassName: '', classCode: '', description: '' };

const FAClassList = () => {
  const navigate = useNavigate();
  const [classes, setClasses]         = useState([]);
  const [subclasses, setSubclasses]   = useState([]);
  const [error, setError]             = useState('');
  const [success, setSuccess]         = useState('');
  const [loading, setLoading]         = useState(true);
  const [classDialog, setClassDialog] = useState(false);
  const [subDialog, setSubDialog]     = useState(false);
  const [editingClass, setEditingClass]       = useState(null);
  const [editingSubclass, setEditingSubclass] = useState(null);
  const [classForm, setClassForm]   = useState(emptyClass);
  const [subForm, setSubForm]       = useState(emptySubclass);
  const classGuard = useUnsavedGuard({ open: classDialog, value: classForm, onClose: () => setClassDialog(false) });
  const subGuard   = useUnsavedGuard({ open: subDialog,   value: subForm,   onClose: () => setSubDialog(false) });
  const [saving, setSaving]         = useState(false);
  const [viewClass, setViewClass]   = useState(null);
  const [viewSub,   setViewSub]     = useState(null);
  const [classDlg,    setClassDlg]    = useState(null);
  const [subclassDlg, setSubclassDlg] = useState(null);

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  const fetchAll = async () => {
    try {
      const [cRes, sRes] = await Promise.all([
        axios.get(`${API}/api/fixed-assets/classes`,    { headers }),
        axios.get(`${API}/api/fixed-assets/subclasses`, { headers }),
      ]);
      setClasses(cRes.data);
      setSubclasses(sRes.data);
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []); // eslint-disable-line

  useEffect(() => {
    if (!success) return;
    const t = setTimeout(() => setSuccess(''), 2000);
    return () => clearTimeout(t);
  }, [success]);

  // ── Classes ──────────────────────────────────────────────────────────
  const openCreateClass = () => { setEditingClass(null); setClassForm(emptyClass); setClassDialog(true); };
  const openEditClass   = (c) => { setEditingClass(c.classCode); setClassForm({ ...emptyClass, ...c }); setClassDialog(true); };

  const saveClass = async () => {
    setSaving(true); setError('');
    try {
      if (editingClass) {
        await axios.put(`${API}/api/fixed-assets/classes/${editingClass}`, classForm, { headers });
        setSuccess('FA Class updated');
      } else {
        await axios.post(`${API}/api/fixed-assets/classes`, classForm, { headers });
        setSuccess('FA Class created');
      }
      setClassDialog(false); fetchAll();
    } catch (err) { setError(err.response?.data?.error || 'Save failed'); }
    finally { setSaving(false); }
  };

  const handleDeleteClass = (code) => {
    setClassDlg(code);
  };

  const confirmDeleteClass = async () => {
    try {
      await axios.delete(`${API}/api/fixed-assets/classes/${classDlg}`, { headers });
      setSuccess('Deleted');
      setClassDlg(null);
      fetchAll();
    } catch (err) {
      setError(err.response?.data?.error || 'Delete failed');
      setClassDlg(null);
    }
  };

  // ── Subclasses ───────────────────────────────────────────────────────
  const openCreateSub = (classCode = '') => {
    setEditingSubclass(null);
    setSubForm({ ...emptySubclass, classCode });
    setSubDialog(true);
  };
  const openEditSub = (s) => {
    setEditingSubclass(s.subclassCode);
    setSubForm({ ...emptySubclass, ...s });
    setSubDialog(true);
  };

  const saveSub = async () => {
    setSaving(true); setError('');
    try {
      if (editingSubclass) {
        await axios.put(`${API}/api/fixed-assets/subclasses/${editingSubclass}`, subForm, { headers });
        setSuccess('Subclass updated');
      } else {
        await axios.post(`${API}/api/fixed-assets/subclasses`, subForm, { headers });
        setSuccess('Subclass created');
      }
      setSubDialog(false); fetchAll();
    } catch (err) { setError(err.response?.data?.error || 'Save failed'); }
    finally { setSaving(false); }
  };

  const handleDeleteSub = (code) => {
    setSubclassDlg(code);
  };

  const confirmDeleteSubclass = async () => {
    try {
      await axios.delete(`${API}/api/fixed-assets/subclasses/${subclassDlg}`, { headers });
      setSuccess('Deleted');
      setSubclassDlg(null);
      fetchAll();
    } catch (err) {
      setError(err.response?.data?.error || 'Delete failed');
      setSubclassDlg(null);
    }
  };

  if (loading) return (
    <Box sx={{ maxWidth: 900, mx: 'auto', mt: 4, textAlign: 'center' }}>
      <CircularProgress /><Typography sx={{ mt: 2 }}>Loading...</Typography>
    </Box>
  );

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto', mt: 4 }}>
      <Card elevation={3}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Box>
              <Typography variant="h5" color="primary">FA Classes & Subclasses</Typography>
              <Typography variant="body2" color="text.secondary">Classify your fixed assets for reporting</Typography>
            </Box>
            <Box display="flex" gap={1}>
              <Button size="small" variant="outlined" startIcon={<Add />} onClick={openCreateClass}>New Class</Button>
              <Button size="small" variant="outlined" startIcon={<Add />} onClick={() => openCreateSub()}>New Subclass</Button>
              <IconButton onClick={() => navigate('/fixed-assets')} title="Back to Fixed Assets">
                <ArrowBack />
              </IconButton>
            </Box>
          </Box>

          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
          {error   && <Alert severity="error"   sx={{ mb: 2 }}>{error}</Alert>}

          {/* Classes + their subclasses grouped */}
          {classes.map(c => {
            const subs = subclasses.filter(s => s.classCode === c.classCode);
            return (
              <Box key={c.classCode} mb={3}>
                <Box display="flex" alignItems="center" justifyContent="space-between"
                  sx={{ bgcolor: 'grey.100', px: 2, py: 1, borderRadius: 1, cursor: 'pointer', '&:hover': { bgcolor: 'grey.200' } }}
                  onClick={() => setViewClass(c)}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Chip label={c.classCode} size="small" color="primary" />
                    <Typography fontWeight={700}>{c.className}</Typography>
                    {c.description && <Typography variant="caption" color="text.secondary">— {c.description}</Typography>}
                  </Box>
                  <Box onClick={e => e.stopPropagation()}>
                    <IconButton size="small" onClick={() => openEditClass(c)}><Edit fontSize="small" /></IconButton>
                    <IconButton size="small" color="error" onClick={() => handleDeleteClass(c.classCode)}><Delete fontSize="small" /></IconButton>
                    <Button size="small" sx={{ ml: 1 }} onClick={() => openCreateSub(c.classCode)} startIcon={<Add />}>
                      Subclass
                    </Button>
                  </Box>
                </Box>

                {subs.length > 0 ? (
                  <Table size="small" sx={{ ml: 2 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Code</strong></TableCell>
                        <TableCell><strong>Subclass Name</strong></TableCell>
                        <TableCell><strong>Description</strong></TableCell>
                        <TableCell><strong>Actions</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {subs.map(s => (
                        <TableRow key={s.subclassCode} hover sx={{ cursor: 'pointer' }}
                          onClick={() => setViewSub({ ...s, className: c.className })}>
                          <TableCell><Chip label={s.subclassCode} size="small" variant="outlined" /></TableCell>
                          <TableCell>{s.subclassName}</TableCell>
                          <TableCell><Typography variant="caption">{s.description}</Typography></TableCell>
                          <TableCell onClick={e => e.stopPropagation()}>
                            <IconButton size="small" onClick={() => openEditSub(s)}><Edit fontSize="small" /></IconButton>
                            <IconButton size="small" color="error" onClick={() => handleDeleteSub(s.subclassCode)}><Delete fontSize="small" /></IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 2, display: 'block', mt: 0.5 }}>
                    No subclasses yet
                  </Typography>
                )}
                <Divider sx={{ mt: 1 }} />
              </Box>
            );
          })}

          {classes.length === 0 && (
            <Typography color="text.secondary">No FA classes yet. Click "New Class" to get started.</Typography>
          )}
        </CardContent>
      </Card>

      {/* ── FA Class Card ── */}
      <Dialog open={Boolean(viewClass)} onClose={() => setViewClass(null)} maxWidth="sm" fullWidth>
        {viewClass && (
          <>
            <DialogTitle component="div">
              <Box display="flex" alignItems="center" gap={1}>
                <Category color="primary" />
                <Box>
                  <Typography variant="h6" lineHeight={1}>{viewClass.className}</Typography>
                  <Typography variant="caption" color="text.secondary">{viewClass.classCode} · FA Class</Typography>
                </Box>
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              <Typography variant="subtitle2" color="primary" gutterBottom>Classification</Typography>
              <InfoRow label="Class Code"   value={viewClass.classCode} />
              <InfoRow label="Class Name"   value={viewClass.className} />
              <InfoRow label="Description"  value={viewClass.description} />

              <Divider sx={{ my: 1.5 }} />

              <Typography variant="subtitle2" color="primary" gutterBottom>Subclasses</Typography>
              <InfoRow label="Subclass Count"
                value={subclasses.filter(s => s.classCode === viewClass.classCode).length} />
            </DialogContent>
            <DialogActions>
              <Button startIcon={<Add />}
                onClick={() => { setViewClass(null); openCreateSub(viewClass.classCode); }}>
                Add Subclass
              </Button>
              <Button startIcon={<Edit />}
                onClick={() => { setViewClass(null); openEditClass(viewClass); }}>
                Edit
              </Button>
              <Button variant="outlined" onClick={() => setViewClass(null)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* ── FA Subclass Card ── */}
      <Dialog open={Boolean(viewSub)} onClose={() => setViewSub(null)} maxWidth="sm" fullWidth>
        {viewSub && (
          <>
            <DialogTitle component="div">
              <Box display="flex" alignItems="center" gap={1}>
                <Label color="secondary" />
                <Box>
                  <Typography variant="h6" lineHeight={1}>{viewSub.subclassName}</Typography>
                  <Typography variant="caption" color="text.secondary">{viewSub.subclassCode} · Subclass of {viewSub.classCode}</Typography>
                </Box>
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              <Typography variant="subtitle2" color="primary" gutterBottom>Subclass Details</Typography>
              <InfoRow label="Subclass Code" value={viewSub.subclassCode} />
              <InfoRow label="Subclass Name" value={viewSub.subclassName} />
              <InfoRow label="Description"   value={viewSub.description} />

              <Divider sx={{ my: 1.5 }} />

              <Typography variant="subtitle2" color="primary" gutterBottom>Parent Class</Typography>
              <InfoRow label="Class Code" value={viewSub.classCode} />
              <InfoRow label="Class Name" value={viewSub.className} />
            </DialogContent>
            <DialogActions>
              <Button startIcon={<Edit />}
                onClick={() => { setViewSub(null); openEditSub(viewSub); }}>
                Edit
              </Button>
              <Button variant="outlined" onClick={() => setViewSub(null)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* ── Confirm Delete Class dialog ── */}
      <Dialog open={Boolean(classDlg)} onClose={() => setClassDlg(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main' }}>
          <WarningAmber color="error" /> Confirm Delete
        </DialogTitle>
        <DialogContent>
          <Typography>Delete class <strong>{classDlg}</strong>?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClassDlg(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={confirmDeleteClass}>Delete</Button>
        </DialogActions>
      </Dialog>

      {/* ── Confirm Delete Subclass dialog ── */}
      <Dialog open={Boolean(subclassDlg)} onClose={() => setSubclassDlg(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main' }}>
          <WarningAmber color="error" /> Confirm Delete
        </DialogTitle>
        <DialogContent>
          <Typography>Delete subclass <strong>{subclassDlg}</strong>?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSubclassDlg(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={confirmDeleteSubclass}>Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Class Dialog */}
      {classGuard.discardDialog}
      <Dialog open={classDialog} onClose={classGuard.attemptClose} maxWidth="xs" fullWidth>
        <DialogTitle>{editingClass ? 'Edit FA Class' : 'New FA Class'}</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <TextField label="Class Code *" value={classForm.classCode}
            onChange={e => setClassForm(f => ({ ...f, classCode: e.target.value }))}
            disabled={!!editingClass} fullWidth size="small" sx={{ mb: 2 }} />
          <TextField label="Class Name *" value={classForm.className}
            onChange={e => setClassForm(f => ({ ...f, className: e.target.value }))}
            fullWidth size="small" sx={{ mb: 2 }} />
          <TextField label="Description" value={classForm.description}
            onChange={e => setClassForm(f => ({ ...f, description: e.target.value }))}
            fullWidth size="small" />
        </DialogContent>
        <DialogActions>
          <Button onClick={classGuard.attemptClose}>Cancel</Button>
          <Button variant="contained" onClick={saveClass} disabled={saving}>{saving ? 'Saving...' : 'Save'}</Button>
        </DialogActions>
      </Dialog>

      {/* Subclass Dialog */}
      {subGuard.discardDialog}
      <Dialog open={subDialog} onClose={subGuard.attemptClose} maxWidth="xs" fullWidth>
        <DialogTitle>{editingSubclass ? 'Edit Subclass' : 'New FA Subclass'}</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <TextField select label="Parent Class *" value={subForm.classCode}
            onChange={e => setSubForm(f => ({ ...f, classCode: e.target.value }))}
            fullWidth size="small" sx={{ mb: 2 }}>
            {classes.map(c => <MenuItem key={c.classCode} value={c.classCode}>{c.classCode} — {c.className}</MenuItem>)}
          </TextField>
          <TextField label="Subclass Code *" value={subForm.subclassCode}
            onChange={e => setSubForm(f => ({ ...f, subclassCode: e.target.value }))}
            disabled={!!editingSubclass} fullWidth size="small" sx={{ mb: 2 }} />
          <TextField label="Subclass Name *" value={subForm.subclassName}
            onChange={e => setSubForm(f => ({ ...f, subclassName: e.target.value }))}
            fullWidth size="small" sx={{ mb: 2 }} />
          <TextField label="Description" value={subForm.description}
            onChange={e => setSubForm(f => ({ ...f, description: e.target.value }))}
            fullWidth size="small" />
        </DialogContent>
        <DialogActions>
          <Button onClick={subGuard.attemptClose}>Cancel</Button>
          <Button variant="contained" onClick={saveSub} disabled={saving}>{saving ? 'Saving...' : 'Save'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FAClassList;
